/**
 * Main Navigator
 * Navigation stack for authenticated users with Claude-style sidebar
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { createStackNavigator } from '@react-navigation/stack';
import { useNavigation } from '@react-navigation/native';
import AuthGuard from '../components/auth/AuthGuard';
import AppLayout from '../components/layout/AppLayout';
import { SidebarRoute } from '../components/navigation/SidebarMenu';
import { useAuthStore } from '../store/authStore';
import { useOnboardingStore } from '../store/onboardingStore';
import { storage } from '../utils/storage';
import Loading from '../components/common/Loading';
import logger from '../utils/logger';

// Screens
import DashboardScreen from '../screens/DashboardScreen';
import ConversationScreen from '../screens/ConversationScreen';
import FinancialGoalsScreen from '../screens/FinancialGoalsScreen';
import BudgetScreen from '../screens/BudgetScreen';
import AccountsScreen from '../screens/AccountsScreen';
import ProfileScreen from '../screens/ProfileScreen';
import EditProfileScreen from '../screens/EditProfileScreen';
import EditFinancialProfileScreen from '../screens/EditFinancialProfileScreen';
import FinancialDiagnosticWizardScreen from '../screens/FinancialDiagnosticWizardScreen';
import ImportBankStatementsWizardScreen from '../screens/ImportBankStatementsWizardScreen';
import SubscriptionSuccessScreen from '../screens/SubscriptionSuccessScreen';
import SubscriptionCancelScreen from '../screens/SubscriptionCancelScreen';
import BankConnectionScreen from '../screens/BankConnectionScreen';
import ReconciliationScreen from '../screens/ReconciliationScreen';

const Stack = createStackNavigator();

// Map sidebar routes to screen components
const routeScreenMap: Record<SidebarRoute, React.ComponentType<any>> = {
  Dashboard: DashboardScreen,
  Chats: ConversationScreen,
  Goals: FinancialGoalsScreen,
  Budget: BudgetScreen,
  Accounts: AccountsScreen,
  Settings: ProfileScreen,
};

// Route titles for header
const routeTitles: Record<SidebarRoute, string> = {
  Dashboard: 'Dashboard',
  Chats: 'Chats',
  Goals: 'Objectifs',
  Budget: 'Budget',
  Accounts: 'Mes Comptes',
  Settings: 'Paramètres',
};

/**
 * Main content area with sidebar navigation
 *
 * Default route logic:
 * - If initialRoute param is set (e.g. from FinancialDiagnosticWizard): use that route
 * - If first time after onboarding (hasCompletedFirstSession = false): Chat
 * - On subsequent app opens (hasCompletedFirstSession = true): Dashboard
 */
function MainContent({ route }: { route?: any }) {
  const initialRoute = route?.params?.initialRoute as SidebarRoute | undefined;
  const [activeRoute, setActiveRoute] = useState<SidebarRoute | null>(
    initialRoute || null
  );
  const [isLoadingDefault, setIsLoadingDefault] = useState(!initialRoute);
  const hasMarkedFirstSession = useRef(false);
  const [screenParams, setScreenParams] = useState<any>(null);
  const navigation = useNavigation<any>();
  const { logout } = useAuthStore();
  const { consumePendingNavigationAction } = useOnboardingStore();

  // Determine default route based on first-session flag
  useEffect(() => {
    if (initialRoute) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const hasCompleted = await storage.getHasCompletedFirstSession();
        if (!cancelled) {
          const defaultRoute: SidebarRoute = hasCompleted ? 'Dashboard' : 'Chats';
          logger.log('[MainContent] Default route resolved:', {
            hasCompletedFirstSession: hasCompleted,
            defaultRoute,
          });
          setActiveRoute(defaultRoute);
          setIsLoadingDefault(false);
        }
      } catch (err) {
        logger.error('[MainContent] Error loading first-session flag:', err);
        if (!cancelled) {
          setActiveRoute('Chats');
          setIsLoadingDefault(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [initialRoute]);

  // Mark first session as completed once Chat is shown post-onboarding
  useEffect(() => {
    if (activeRoute === 'Chats' && !hasMarkedFirstSession.current) {
      hasMarkedFirstSession.current = true;
      storage.setHasCompletedFirstSession().then(() => {
        logger.log('[MainContent] First session marked as completed');
      }).catch((err: any) => {
        logger.error('[MainContent] Error saving first-session flag:', err);
      });
    }
  }, [activeRoute]);

  // Update document title on web when route changes
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined' && activeRoute) {
      document.title = `${routeTitles[activeRoute]} - Wealfi`;
    }
  }, [activeRoute]);

  // Handle pending diagnostic action from onboarding completion
  useEffect(() => {
    if (!isLoadingDefault && activeRoute) {
      const pendingAction = consumePendingNavigationAction();
      if (pendingAction) {
        logger.log('[MainContent] Pending diagnostic action:', pendingAction);
        const actionScreenMap: Record<string, string> = {
          connect_bank: 'BankConnection',
          import_pdf: 'ImportBankStatementsWizard',
          manual_snapshot: 'FinancialDiagnosticWizard',
        };
        const targetScreen = actionScreenMap[pendingAction];
        if (targetScreen) {
          // Short delay to let the main screen mount first
          setTimeout(() => navigation.navigate(targetScreen), 300);
        }
      }
    }
  }, [isLoadingDefault, activeRoute]);

  // Handle navigation params changes (e.g., from FinancialDiagnosticWizard)
  useEffect(() => {
    if (route?.params?.initialRoute) {
      setActiveRoute(route.params.initialRoute);
      if (route.params.scrollToDiagnostic) {
        setScreenParams({
          scrollToDiagnostic: true,
          diagnosticTimestamp: route.params.diagnosticTimestamp,
        });
      }
      if (route.params.importCompleteTimestamp) {
        setScreenParams({
          importComplete: true,
          importCompleteTimestamp: route.params.importCompleteTimestamp,
        });
      }
      if (route.params.triggerScanner) {
        setScreenParams({
          triggerScanner: true,
          scannerTimestamp: route.params.scannerTimestamp || Date.now(),
        });
      }
    }
  }, [route?.params?.initialRoute, route?.params?.scrollToDiagnostic, route?.params?.diagnosticTimestamp, route?.params?.importCompleteTimestamp, route?.params?.triggerScanner, route?.params?.scannerTimestamp]);

  // Clear screen params after passing them
  useEffect(() => {
    if (screenParams && activeRoute === 'Chats') {
      const timer = setTimeout(() => setScreenParams(null), 100);
      return () => clearTimeout(timer);
    }
  }, [screenParams, activeRoute]);

  const handleNavigate = useCallback((route: SidebarRoute) => {
    setActiveRoute(route);
  }, []);

  const handleSettingsPress = useCallback(() => {
    setActiveRoute('Settings');
  }, []);

  const handleSignOut = useCallback(async () => {
    await logout();
  }, [logout]);

  // Show loading while resolving default route (AFTER all hooks)
  if (isLoadingDefault || !activeRoute) {
    return <Loading fullScreen message="Chargement..." />;
  }

  const CurrentScreen = routeScreenMap[activeRoute];
  const currentScreenParams = activeRoute === 'Chats' ? screenParams : null;

  return (
    <AppLayout
      activeRoute={activeRoute}
      onNavigate={handleNavigate}
      onSettingsPress={handleSettingsPress}
      onSignOut={handleSignOut}
      headerTitle={routeTitles[activeRoute]}
    >
      <CurrentScreen
        scrollToDiagnostic={currentScreenParams?.scrollToDiagnostic}
        triggerScanner={currentScreenParams?.triggerScanner}
      />
    </AppLayout>
  );
}

/**
 * Main Navigator with stack for modals
 */
export default function MainNavigator() {
  return (
    <AuthGuard>
      <Stack.Navigator
        screenOptions={{
          headerShown: false,
        }}
      >
        <Stack.Screen
          name="MainContent"
          component={MainContent}
          options={{ title: 'Wealfi' }}
        />
        <Stack.Screen
          name="EditProfile"
          component={EditProfileScreen}
          options={{ title: 'Modifier le profil - Wealfi' }}
        />
        <Stack.Screen
          name="EditFinancialProfile"
          component={EditFinancialProfileScreen}
          options={{ title: 'Profil Financier - Wealfi' }}
        />
        <Stack.Screen
          name="FinancialDiagnosticWizard"
          component={FinancialDiagnosticWizardScreen}
          options={{
            headerShown: false,
            presentation: 'modal',
          }}
        />
        <Stack.Screen
          name="ImportBankStatementsWizard"
          component={ImportBankStatementsWizardScreen}
          options={{
            headerShown: false,
            presentation: 'modal',
          }}
        />
        <Stack.Screen
          name="SubscriptionSuccess"
          component={SubscriptionSuccessScreen}
          options={{
            headerShown: false,
            title: 'Abonnement Activé - Wealfi',
          }}
        />
        <Stack.Screen
          name="SubscriptionCancel"
          component={SubscriptionCancelScreen}
          options={{
            headerShown: false,
            title: 'Paiement Annulé - Wealfi',
          }}
        />
        <Stack.Screen
          name="BankConnection"
          component={BankConnectionScreen}
          options={{
            headerShown: false,
            presentation: 'modal',
            title: 'Connexion Bancaire - Wealfi',
          }}
        />
        <Stack.Screen
          name="Reconciliation"
          component={ReconciliationScreen}
          options={{
            headerShown: false,
            title: 'Réconciliation Budget - Wealfi',
          }}
        />
      </Stack.Navigator>
    </AuthGuard>
  );
}
